import React, { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Wand2, ShieldCheck } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

export const useJob = (onDone) => {
  const [job, setJob] = useState(null);
  const timer = useRef(null);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;

  const poll = useCallback((id) => {
    clearInterval(timer.current);
    timer.current = setInterval(async () => {
      try {
        const r = await api.get(`/admin/curios/workbench/jobs/${id}`);
        setJob(r.data);
        if (r.data.status === "done") { clearInterval(timer.current); doneRef.current?.(r.data); }
      } catch { clearInterval(timer.current); }
    }, 2500);
  }, []);

  useEffect(() => () => clearInterval(timer.current), []);

  const start = async (url, body) => {
    const r = await api.post(url, body || {});
    if (!r.data.job_id) return r.data;
    setJob({ total: r.data.total, done: 0, failed: 0, status: r.data.total ? "running" : "done" });
    if (r.data.total) poll(r.data.job_id);
    return r.data;
  };
  const running = job?.status === "running";
  return { job, start, running };
};

export const JobProgress = ({ job, testid }) => job && job.total > 0 && (
  <span data-testid={testid} className="font-mono-cv text-[11px] text-[#7A6C5D]">
    {job.done}/{job.total}{job.failed ? ` · ${job.failed} failed` : ""}{job.status === "done" ? " · done" : ""}
  </span>
);

const BTN = "inline-flex items-center gap-1.5 border border-[#D8CEB9] bg-white text-[#4A4036] font-ui text-xs font-semibold px-3 py-1.5 rounded-sm hover:border-[#9E4535] hover:text-[#9E4535] disabled:opacity-50";

export const BulkImagesButton = ({ onDone }) => {
  const [count, setCount] = useState(null);
  const refresh = () => api.get("/admin/curios/workbench/missing-images").then((r) => setCount(r.data.count)).catch(() => {});
  const { job, start, running } = useJob((j) => { toast.success(`Generated ${j.done - j.failed} engravings`); refresh(); onDone(); });
  useEffect(() => { refresh(); }, []);
  const go = async () => {
    try { await start("/admin/curios/workbench/generate-missing-images"); } catch { toast.error("Could not start image generation"); }
  };
  return (
    <div className="inline-flex items-center gap-2">
      <button data-testid="wb-generate-missing-images" onClick={go} disabled={running || !count} className={BTN}>
        {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
        Generate missing images ({count ?? "…"})
      </button>
      <JobProgress job={job} testid="wb-images-progress" />
    </div>
  );
};

export const VerifySourcesButton = ({ onDone }) => {
  const { job, start, running } = useJob((j) => { toast.success(`Checked sources on ${j.done} curios`); onDone(); });
  return (
    <div className="inline-flex items-center gap-2">
      <button data-testid="wb-verify-sources" onClick={() => start("/admin/curios/workbench/verify-sources").catch(() => toast.error("Could not start"))} disabled={running} className={BTN}>
        {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />} Re-verify all sources
      </button>
      <JobProgress job={job} testid="wb-verify-progress" />
    </div>
  );
};
